import { Component, Input, ViewChild, ElementRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DynamicNumberedContentComponent } from '../../../../shared/components/dynamic-numbered-content/dynamic-numbered-content';
import { IDevelopmentProject } from '../../../../shared/interfaces/development-project.interface';
import { IMarketingProject } from '../../../../shared/interfaces/marketing-project.interface';

@Component({
  selector: 'app-project-details',
  standalone: true,
  imports: [CommonModule, DynamicNumberedContentComponent],
  templateUrl: './project-details.html',
  styleUrl: './project-details.scss'
})
export class ProjectDetails {
  @ViewChild('carousel', { static: false }) carousel!: ElementRef;

  @Input() project!: IDevelopmentProject | IMarketingProject;

  isDragging = false;
  startX = 0;
  scrollStart = 0;

  isUserInteracting = false;
  scrollDirection = 1;
  rafId: any;
  resumeTimeout: any;
  bounceCooldown = false;

  constructor(private ngZone: NgZone) {}

  ngAfterViewInit() {
    const el = this.carousel.nativeElement;
    el.querySelectorAll('img').forEach((img: HTMLImageElement) => {
      img.draggable = false;
      img.addEventListener('dragstart', e => e.preventDefault());
    });
    setTimeout(() => this.startAutoScroll(), 15000);
  }

  startAutoScroll() {
    this.ngZone.runOutsideAngular(() => {
      const step = () => {
        if (!this.isDragging && !this.isUserInteracting) {
          const el = this.carousel.nativeElement;
          const speed = el.clientWidth * 0.001;
          el.scrollLeft += this.scrollDirection * speed;

          const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 3;
          const atStart = el.scrollLeft <= 3;

          if (!this.bounceCooldown && (atEnd || atStart)) {
            this.bounceCooldown = true;
            setTimeout(() => {
              this.scrollDirection = atEnd ? -1 : 1;
              this.bounceCooldown = false;
            }, 15000);
          }
        }
        this.rafId = requestAnimationFrame(step);
      };
      this.rafId = requestAnimationFrame(step);
    });
  }

  stopAutoScrollTemp() {
    this.isUserInteracting = true;
    clearTimeout(this.resumeTimeout);
    this.resumeTimeout = setTimeout(() => {
      this.isUserInteracting = false;
    }, 30000);
  }

  onPointerDown(event: PointerEvent) {
    this.stopAutoScrollTemp();
    this.isDragging = true;
    const el = this.carousel.nativeElement;
    if (event.pointerId != null && el.setPointerCapture) {
      try { el.setPointerCapture(event.pointerId); } catch {}
    }
    this.startX = event.clientX - el.getBoundingClientRect().left;
    this.scrollStart = el.scrollLeft;
    el.classList.add('cursor-grabbing');
  }

  onPointerUp(event: PointerEvent) {
    this.isDragging = false;
    const el = this.carousel.nativeElement;
    if (event.pointerId != null && el.releasePointerCapture) {
      try { el.releasePointerCapture(event.pointerId); } catch {}
    }
    el.classList.remove('cursor-grabbing');
  }

  onPointerMove(event: PointerEvent) {
    if (!this.isDragging) return;
    if (event.buttons !== undefined && event.buttons !== 1) return;
    event.preventDefault();
    const el = this.carousel.nativeElement;
    const x = event.clientX - el.getBoundingClientRect().left;
    const baseMove = x - this.startX;
    const firstImg: HTMLImageElement = el.querySelector('img');
    const imgWidth = firstImg ? firstImg.clientWidth : 300;
    const multiplier = window.innerWidth > 1024 ? imgWidth / 40 : imgWidth / 80;
    const walk = baseMove * multiplier;
    el.scrollLeft = this.scrollStart - walk;
  }

  onCarouselClick(event: MouseEvent) {
    this.stopAutoScrollTemp();
    const el = this.carousel.nativeElement;
    const rect = el.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    if (clickX < rect.width / 2) {
      el.scrollBy({ left: -300, behavior: 'smooth' });
      this.scrollDirection = -1;
    } else {
      el.scrollBy({ left: 300, behavior: 'smooth' });
      this.scrollDirection = 1;
    }
  }
}
